import { toast } from "@/components/common/Toast"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { isLimitError } from "@/utils/limitErrors"
import {
  normalizeFrameworkCode,
  regulationLabel,
} from "../utils/regulationLabel"

// The apiClient interceptor auto-toasts 5xx errors only; 4xx business errors
// (e.g. { detail: { code, message } }) are surfaced by the caller. This reads
// that message off an axios-style error so it can be shown as-is.
//
// Kept exported for callers outside features/material-compliance. Inside this
// feature the UI never shows backend prose, use `apiErrorMessage` instead.
export function getApiErrorMessage(error: unknown): string | undefined {
  return (
    error as {
      response?: { data?: { detail?: { message?: string } } }
    }
  )?.response?.data?.detail?.message
}

/**
 * Reads a 4xx business error's `detail.code` off an axios-shaped error.
 * `detail` can also be a plain string (some endpoints) or absent entirely
 * (a network failure, a >=500 already toasted by the global interceptor),
 * both read as null, same as an unrecognised code would.
 */
export function getApiErrorCode(error: unknown): string | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  if (typeof detail !== "object" || detail === null) return null
  const code = (detail as { code?: unknown }).code
  return typeof code === "string" ? code : null
}

/**
 * A 4xx business error's whole `detail` object, for the fields beyond `code`.
 * Several endpoints answer a conflict with the id of the row that caused it
 * (`link_id`, `customer_id`) precisely so the UI can offer that row instead of
 * dead-ending on "already exists" — reading only the code throws that away.
 * Null for a string detail, a 422's array, or no detail at all.
 */
export function getApiErrorDetail(
  error: unknown,
): Record<string, unknown> | null {
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  if (typeof detail !== "object" || detail === null || Array.isArray(detail))
    return null
  return detail as Record<string, unknown>
}

/** A string field off that detail, or null. */
export function getApiErrorDetailId(
  error: unknown,
  field: string,
): string | null {
  const value = getApiErrorDetail(error)?.[field]
  return typeof value === "string" ? value : null
}

/**
 * Backend `detail.code` -> existing translated copy. Only codes with real,
 * already-catalogued copy that reads correctly at ANY call site belong here.
 * A code whose only existing copy is specific to one screen's controls
 * (e.g. ALREADY_APPROVED, which names "Request"/"Skip" only the review
 * board has) stays out and is left to each caller's own fallback key.
 */
export const API_ERROR_KEYS: Partial<Record<string, TranslationKeys>> = {
  SUPPLIER_NUMBER_TAKEN: "MC_DECL_V2_SUP_NUMBER_TAKEN",
  RECOMPUTE_IN_PROGRESS: "MC_DECL_V2_SUP_RECOMPUTE_IN_PROGRESS",
  UNSUPPORTED_FILE_TYPE: "MC_DECL_DROP_UNSUPPORTED",
  NO_INQUIRY_REGULATIONS: "MC_INQ_NO_REGULATIONS_ERROR",
  // Inquiry regulation picker (TUR-1730)
  EMPTY_REGULATION_PICK: "MC_INQ_ERR_EMPTY_REGULATION_PICK",
  INVALID_REGULATION_KEY: "MC_INQ_ERR_INVALID_REGULATION_KEY",
  REGULATION_SETTINGS_OFF: "MC_INQ_ERR_REGULATION_SETTINGS_OFF",
  REGULATION_SETTINGS_UNAVAILABLE: "MC_INQ_ERR_REGULATION_SETTINGS_UNAVAILABLE",
  // Waiver of what a partial answer left open (TUR-1732)
  NOTHING_TO_WAIVE: "MC_INQ_ERR_NOTHING_TO_WAIVE",
  // Inquiry sending address
  INVALID_SLUG: "MC_TPL_EMAIL_ADDRESS_INVALID",
  RESERVED_SLUG: "SETTINGS/EMAIL_SENDING/ERR_RESERVED_SLUG",
  SLUG_TAKEN: "SETTINGS/EMAIL_SENDING/ERR_SLUG_TAKEN",
  // Inquiry email templates and Cc
  INVALID_DEFAULT_CC: "SETTINGS/SUPPLIER_INQUIRIES/ERR_INVALID_CC",
  // Supplier e-mail on save, inquiry recipients on send
  INVALID_EMAIL: "MC_API_ERROR_INVALID_EMAIL",
  INVALID_RECIPIENT: "MC_API_ERROR_INVALID_RECIPIENT",
  INVALID_TEMPLATE_VARIABLE: "SETTINGS/SUPPLIER_INQUIRIES/ERR_UNKNOWN_VARIABLE",
  RESERVED_REFERENCE_TOKEN: "SETTINGS/SUPPLIER_INQUIRIES/REFERENCE_TOKEN",
  // The declaration row is held (an approval's decisive part, another save, a
  // sign-off): the write waited its bounded time, was rolled back and wrote
  // nothing (TUR-1725). True at every call site: "not saved yet, try again".
  REVIEW_BUSY: "MC_API_ERROR_REVIEW_BUSY",
}

/**
 * FastAPI's request-validation failures answer with `detail: [{ loc, msg }]` —
 * an ARRAY, not the `{ code, message }` of a business 4xx. `getApiErrorCode`
 * therefore reads null for them and every one of these collapsed into the
 * caller's generic fallback: the editor said the save failed and never said
 * which field was wrong, so the only way forward was to guess.
 *
 * This reads the offending field names off that shape. `loc` is a path
 * (`["body", "contact_email"]`); the last segment is the field, and a numeric
 * last segment (a list index) means the field is the one before it. Names are
 * humanised (`contact_email` -> `contact email`) rather than translated: a
 * per-field catalog would have to track every schema in the API, and a
 * recognisable field name beats a generic failure.
 */
export function getValidationFields(error: unknown): string[] {
  if (getApiErrorStatus(error) !== 422) return []
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  if (!Array.isArray(detail)) return []

  const names = detail.flatMap((item) => {
    const loc = (item as { loc?: unknown })?.loc
    if (!Array.isArray(loc) || loc.length === 0) return []
    // Walk back past list indices to the last named segment.
    const named = [...loc].reverse().find((part) => typeof part === "string")
    // "body" / "query" are the request part, not a field the user filled in.
    if (typeof named !== "string" || ["body", "query", "path"].includes(named))
      return []
    return [named.replaceAll("_", " ")]
  })

  return [...new Set(names)]
}

/** A 422 item that refused a list for its SIZE, with the numbers it carries. */
export interface ValidationSizeProblem {
  /** The humanised field name, same shape `getValidationFields` returns. */
  field: string
  /** The cap the schema declares. */
  max: number
  /** How many items were actually sent, when the error says. */
  actual: number | null
}

/**
 * "Please check this field: article numbers" is a LIE for a size refusal: the
 * field is not wrong, there are simply more items in it than the route accepts,
 * and no amount of checking it tells the reviewer that. A 10,582-article
 * declaration hit exactly this in prod (2026-10-28, `body.article_numbers
 * too_long`): the editor said to check the field, the Apply button stayed
 * enabled, and people clicked it again.
 *
 * Pydantic v2 says so precisely — `type: "too_long"` with
 * `ctx: { max_length, actual_length }` — so when the shape is there the copy can
 * name the real problem and both numbers. When `ctx` is absent (a route that
 * strips it) this reads null and the caller falls back to the field-name copy,
 * which is no worse than today.
 */
export function getValidationSizeProblem(
  error: unknown,
): ValidationSizeProblem | null {
  if (getApiErrorStatus(error) !== 422) return null
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  if (!Array.isArray(detail)) return null

  for (const item of detail) {
    // `too_long` only: it is the one validation type whose copy ("there are too
    // many of these") is right at every call site. A string length, a range or a
    // pattern still reads as "check this field", which is true for those.
    if ((item as { type?: unknown })?.type !== "too_long") continue
    const ctx = (item as { ctx?: unknown })?.ctx
    if (typeof ctx !== "object" || ctx === null) continue
    const max = (ctx as { max_length?: unknown }).max_length
    if (typeof max !== "number") continue
    const actual = (ctx as { actual_length?: unknown }).actual_length
    const loc = (item as { loc?: unknown })?.loc
    const named = Array.isArray(loc)
      ? [...loc]
          .reverse()
          .find(
            (part) =>
              typeof part === "string" &&
              !["body", "query", "path"].includes(part),
          )
      : undefined
    if (typeof named !== "string") continue
    return {
      field: named.replaceAll("_", " "),
      max,
      actual: typeof actual === "number" ? actual : null,
    }
  }
  return null
}

/**
 * The one place `features/material-compliance` reads an API error for
 * display: translated copy for a known `detail.code`, else the caller's own
 * fallback key, filled with the caller's `fallbackValues`. Never the raw
 * `detail.message`, that is backend prose and the UI never shows it.
 */
export function apiErrorMessage(
  error: unknown,
  translate: (
    key: TranslationKeys,
    values?: Record<string, string | number>,
  ) => string,
  fallbackKey: TranslationKeys,
  fallbackValues?: Record<string, string | number>,
): string {
  const code = getApiErrorCode(error)
  const named =
    code === "NO_INQUIRY_REGULATIONS" && namedRegulationsError(error, translate)
  if (named) return named
  const key = code ? API_ERROR_KEYS[code] : undefined
  if (key) return translate(key)

  // Before the field-name copy: a size refusal names a field that is not wrong.
  const size = getValidationSizeProblem(error)
  if (size)
    return translate(
      size.actual === null
        ? "MC_API_ERROR_FIELD_TOO_LONG"
        : "MC_API_ERROR_FIELD_TOO_LONG_COUNTED",
      { field: size.field, max: size.max, actual: size.actual ?? 0 },
    )

  const fields = getValidationFields(error)
  if (fields.length > 0)
    return translate("MC_API_ERROR_INVALID_FIELDS", {
      count: fields.length,
      fields: fields.join(", "),
    })

  return translate(fallbackKey, fallbackValues)
}

const FORMAT_LABEL_KEYS: Partial<Record<string, TranslationKeys>> = {
  pdf: "MC_INQ_DRAFT_FORMAT_PDF",
  excel: "MC_INQ_DRAFT_FORMAT_EXCEL",
  pdf_and_excel: "MC_INQ_DRAFT_FORMAT_PDF_EXCEL",
}

/** A NO_INQUIRY_REGULATIONS refusal that names the format and the regulations it
 *  covers none of (TUR-1730), or null for an older payload without them. */
function namedRegulationsError(
  error: unknown,
  translate: (
    key: TranslationKeys,
    values?: Record<string, string | number>,
  ) => string,
): string | null {
  const detail = getApiErrorDetail(error)
  const formatKey = FORMAT_LABEL_KEYS[String(detail?.attachment_format ?? "")]
  const keys = detail?.regulation_keys
  if (!formatKey || !Array.isArray(keys) || keys.length === 0) return null
  return translate("MC_INQ_ERR_NO_REGULATIONS_NAMED", {
    format: translate(formatKey),
    list: keys
      .map((key) =>
        regulationLabel(
          normalizeFrameworkCode(String(key)),
          String(key),
          translate,
        ),
      )
      .join(", "),
  })
}

/** Reads an axios-style error's HTTP status, or null when there is none
 *  (a network failure, a non-axios throw). Exported for the same reason
 *  `getApiErrorMessage` is: callers outside this feature need it to tell a
 *  status apart before choosing copy. */
export function getApiErrorStatus(error: unknown): number | null {
  const status = (error as { response?: { status?: number } })?.response?.status
  return typeof status === "number" ? status : null
}

/**
 * Is this the FLAG dependency's own refusal, rather than any other 403?
 * `require_flag` (backend `dependencies.py`) answers with a structured
 * detail, `{ message, error_code: "FEATURE_DISABLED" }` — the same contract
 * the global axios interceptor keys on to suppress its server toast.
 *
 * The status alone cannot say it: every flag-guarded route in this feature
 * carries a permission dependency too, and `require_permission` refuses with
 * a 403 whose detail is a bare string (`api/auth.py`). Telling a reviewer the
 * feature is off when their ACCESS was denied sends them to ask for the wrong
 * thing. Note the key is `error_code`, not the `code` of a 4xx business
 * error, so `getApiErrorCode` above never sees it.
 */
export function isFeatureDisabledError(error: unknown): boolean {
  if (getApiErrorStatus(error) !== 403) return false
  const detail = (error as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  if (typeof detail !== "object" || detail === null) return false
  return (detail as { error_code?: unknown }).error_code === "FEATURE_DISABLED"
}

/**
 * The one place `features/material-compliance` toasts an API error: the
 * global axios interceptor (see `errorToast.ts`) already toasts every >=500
 * once, so this stays silent on those and only toasts `apiErrorMessage` for
 * everything else. A limit or trial error (429, trial 403) already got the
 * interceptor's upgrade toast, so it stays silent on those too. Callers that ALSO render the error (setError, a result
 * row) keep calling `apiErrorMessage` directly for that part, this only
 * covers the toast.
 */
export function toastApiError(
  error: unknown,
  translate: (
    key: TranslationKeys,
    values?: Record<string, string | number>,
  ) => string,
  fallbackKey: TranslationKeys,
  fallbackValues?: Record<string, string | number>,
): void {
  const status = getApiErrorStatus(error)
  if (status !== null && status >= 500) return
  if (isLimitError(error)) return
  toast.error(apiErrorMessage(error, translate, fallbackKey, fallbackValues))
}
